import { createApi } from '@reduxjs/toolkit/query/react';
import { toast } from 'react-toastify';
import { createElement } from 'react';
import i18n from '../../i18n';
import axiosPrivate from '@api/axiosPrivate';
import { DEFAULT_PAGE_LIMIT } from '@constants/appDefaults';

const BILLING_PATH = '/settings/billing';

function handleSubscriptionRequired() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname.startsWith(BILLING_PATH)) return;

  toast.error('An active subscription is required to use this feature.');
  window.location.assign(BILLING_PATH);
}

/** Factory: paginated list endpoint (company-scoped, tableFilterOutcomingData) */
export const createPaginatedListEndpoint = ({ url, responseKey, tag }) => ({
  query: ({ companyId, page = 1, limit = DEFAULT_PAGE_LIMIT, tableFilterOutcomingData = {} }) => ({
    url,
    method: 'POST',
    body: { company_id: companyId, page, limit, tableFilterOutcomingData },
  }),
  transformResponse: (response) => ({
    [responseKey]: response?.[responseKey] || [],
    totalPages: response?.totalPages ?? 1,
  }),
  providesTags: [tag],
});

/** Factory: mutation that forwards body to POST endpoint */
export const createBodyMutation = ({ url, invalidatesTags }) => ({
  query: (body) => ({ url, method: 'POST', body }),
  invalidatesTags: Array.isArray(invalidatesTags) ? invalidatesTags : [invalidatesTags],
});

const baseQueryWithAuth = async (args, api, extraOptions) => {
  try {
    const result = await axiosPrivate({
      url: args.url,
      method: args.method || 'POST',
      data: args.body,
      headers: {
        ...args.headers,
      },
    });
    return { data: result.data };
  } catch (axiosError) {
    const status = axiosError.response?.status;
    const data = axiosError.response?.data;

    if (data?.error === 'plan_limit_reached' || data?.code === 'PLAN_SEAT_LIMIT') {
      toast.info(createElement('div', null,
        i18n.t('billing.usage.reached'), ' ',
        createElement('a', { href: `${BILLING_PATH}#plan-comparison` }, i18n.t('billing.usage.upgrade')),
      ), { toastId: 'plan-limit-reached' });
      api.dispatch(apiSlice.util.invalidateTags(['Subscription']));
    }

    if (status === 402 && data?.error === 'subscription_required') {
      handleSubscriptionRequired();
    }

    return {
      error: {
        status,
        data: data || axiosError.message,
      },
    };
  }
};

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['Orders', 'ServiceTypes', 'AccountCodes', 'AccountCodeAmounts', 'ServiceCompanies', 'Users', 'ExpenseCategories', 'InvestmentCategories', 'Customers', 'Invoices', 'Expenses', 'Investments', 'Company', 'ServiceItems', 'Subscription'],
  endpoints: () => ({}),
});
